/**
 * useWaitingVoice
 *
 * Registers WaitingPage with the persistent HandsFreeProvider. The page is
 * poll-driven, so the "screen" is derived from a phase:
 *   - 'choose'  → candidates ready: read the list, listen for a name (or
 *                 "resolver o caso" when allowed).
 *   - 'recap'   → recap available but next episode still loading: read the recap now.
 *   - 'ready'   → next episode prepared: read the recap + "continuar" prompt.
 *   - 'none'    → still processing / out of scope: nothing read.
 *
 * The provider re-reads only when the phase KEY changes, keeps a single realtime
 * listening session across phases, and pauses the mic when leaving.
 */

import { useMemo } from 'react';
import type { EpisodeSummary } from '../services/GameApiService';
import { useHandsFreeScreen } from './useHandsFreeScreen';
import {
  buildCandidateChoiceReadText,
  buildReadyTailReadText,
  buildRecapReadText,
  buildWaitingReadyReadText,
} from './navPrompts';
import {
  NAV_TOOL,
  READ_AGAIN_TOOL,
  NEXT_EPISODE_TOOL,
  RESOLVE_CASE_TOOL,
  buildSelectCharacterTool,
} from './navTools';

export interface WaitingVoiceCandidate {
  id: string;
  name: string;
  role: string;
}

export type WaitingVoiceContext =
  | { phase: 'none' }
  | {
      phase: 'choose';
      candidates: WaitingVoiceCandidate[];
      allowResolve: boolean;
    }
  | {
      phase: 'recap';
      isResolve: boolean;
      recap: { characterName: string; summary: EpisodeSummary };
    }
  | {
      phase: 'ready';
      isResolve: boolean;
      recap?: { characterName: string; summary: EpisodeSummary };
      bridgeText?: string | null;
    };

interface UseWaitingVoiceArgs {
  enabled: boolean;
  token: string;
  context: WaitingVoiceContext;
  onSelectCandidate: (candidateId: string) => void;
  onNextEpisode: () => void;
  onResolveCase: () => void;
}

export function useWaitingVoice({
  enabled,
  token,
  context,
  onSelectCandidate,
  onNextEpisode,
  onResolveCase,
}: UseWaitingVoiceArgs): void {
  const plan = useMemo(() => {
    if (context.phase === 'choose') {
      const ids = context.candidates.map((c) => c.id);
      const tools = [buildSelectCharacterTool(ids), READ_AGAIN_TOOL];
      if (context.allowResolve) tools.push(RESOLVE_CASE_TOOL);
      return {
        active: true,
        readKey: `choose:${ids.join(',')}:${context.allowResolve ? 'r' : ''}`,
        tools,
        readText: buildCandidateChoiceReadText(context.candidates, context.allowResolve),
        continuesReadKey: undefined as string | undefined,
        readTail: undefined as string | undefined,
      };
    }
    if (context.phase === 'recap') {
      return {
        active: true,
        readKey: `recap:${context.recap.characterName}`,
        tools: [READ_AGAIN_TOOL],
        readText: buildRecapReadText({ recap: context.recap }),
        continuesReadKey: undefined as string | undefined,
        readTail: undefined as string | undefined,
      };
    }
    if (context.phase === 'ready') {
      return {
        active: true,
        readKey: `ready:${context.isResolve ? 'resolve' : 'next'}`,
        tools: [NEXT_EPISODE_TOOL, READ_AGAIN_TOOL],
        readText: buildWaitingReadyReadText({ isResolve: context.isResolve, recap: context.recap, bridgeText: context.bridgeText }),
        // If the recap for this episode was already read (or is still playing),
        // don't restart it — just append the tail (bridge + "está pronto").
        continuesReadKey: context.recap ? `recap:${context.recap.characterName}` : undefined,
        readTail: buildReadyTailReadText({ isResolve: context.isResolve, bridgeText: context.bridgeText }),
      };
    }
    return {
      active: false,
      readKey: '',
      tools: [],
      readText: '',
      continuesReadKey: undefined as string | undefined,
      readTail: undefined as string | undefined,
    };
  }, [context]);

  useHandsFreeScreen({
    active: enabled && plan.active,
    token,
    readKey: plan.readKey,
    readText: plan.readText,
    tools: plan.tools,
    continuesReadKey: plan.continuesReadKey,
    readTail: plan.readTail,
    onCommand: (name, args) => {
      if (name === NAV_TOOL.SELECT_CHARACTER) {
        const id = typeof args.candidate_id === 'string' ? args.candidate_id : '';
        if (id) onSelectCandidate(id);
      } else if (name === NAV_TOOL.NEXT_EPISODE) {
        onNextEpisode();
      } else if (name === NAV_TOOL.RESOLVE_CASE) {
        onResolveCase();
      }
    },
  });
}
